import {
  Response,
  BaseTransaction,
  TransactionAML,
  TransactionWarning,
  TransactionWarningActionBody,
  apiAbortController,
} from '@ui/src';
import { StatusUpdate, WarningsInfo } from '../../transactions/type';
import {
  CheckTransaction,
  CheckTransactionResult,
} from '../../../types/check-transaction';
import api from '../../../api/api';
import {
  CreateIncomingTransactionFormValues,
  ExternalAccount,
  IncomingTransactionValidator,
  UpdateIncomingTransaction,
} from '../types';

export const incomingTransactionsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIncomingList: build.query<Response<BaseTransaction>, string>({
      query: (query) => ({
        url: `/v1/transactions-incoming${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIncomingListStatuses: build.query<Response<StatusUpdate>, string>({
      query: (query) => ({
        url: `/v1/transactions-incoming/statuses${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIncomingTransactionById: build.query<{ data: BaseTransaction }, string>({
      query: (id) => ({
        url: `/v1/transactions-incoming/${id}?include=psp-integration-account`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIncomingTransactionValidators: build.query<
      { data: IncomingTransactionValidator },
      void
    >({
      query: () => ({
        url: '/v1/transactions-incoming/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIncomingTransaction: build.mutation<
      { data: BaseTransaction },
      { body: UpdateIncomingTransaction; id: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-incoming/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIncomingTransaction: build.mutation<
      { data: BaseTransaction },
      CreateIncomingTransactionFormValues
    >({
      query: (body) => ({
        url: '/v1/transactions-incoming',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIncomingTransactionAmlById: build.query<
      Response<TransactionAML>,
      string
    >({
      query: (id) => ({
        url: `/v1/scores-results?transactionId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    checkIncomingTransaction: build.mutation<
      { data: CheckTransactionResult },
      {
        body: CheckTransaction;
        abortController: {
          abortPendingRequest: () => void;
          getSignalToken: () => AbortSignal;
        };
      }
    >({
      query: ({ body, abortController }) => ({
        url: `/v1/transactions-incoming/check`,
        method: 'POST',
        body,
        signal: abortController.getSignalToken(),
      }),
    }),
    getIncomingTransactionWarning: build.query<
      { data: TransactionWarning },
      { id: string; warningId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-incoming/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIncomingTransactionWarnings: build.query<
      Response<TransactionWarning>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-incoming/${id}/warnings?take=100`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyIncomingTransactionWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        body: TransactionWarningActionBody;
        transactionId: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-incoming/${data.transactionId}/warnings/${data.warningId}/action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetIncomingTransactionWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        transactionId: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-incoming/${data.transactionId}/warnings/${data.warningId}/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportIncomingTransaction: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/transactions-incoming/export',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsExternalAccountsList: build.query<
      Response<ExternalAccount>,
      string
    >({
      query: (query?: string) => ({
        url: `/v1/psp-integrations-accounts${query || ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIncomingTransactionWarningsInfo: build.query<
      { data: WarningsInfo },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-incoming/${id}/warnings/info`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetIncomingListQuery,
  useLazyGetIncomingListQuery,
  useLazyGetIncomingTransactionByIdQuery,
  useLazyGetIncomingTransactionAmlByIdQuery,
  useUpdateIncomingTransactionMutation,
  useCreateIncomingTransactionMutation,
  useGetIncomingTransactionValidatorsQuery,
  useCheckIncomingTransactionMutation,
  useLazyGetIncomingTransactionWarningsQuery,
  useApplyIncomingTransactionWarningActionMutation,
  useLazyGetIncomingListStatusesQuery,
  useExportIncomingTransactionMutation,
  useLazyGetIncomingTransactionValidatorsQuery,
  useLazyGetTransactionsExternalAccountsListQuery,
  useResetIncomingTransactionWarningActionMutation,
  useLazyGetIncomingTransactionWarningQuery,
  useLazyGetIncomingTransactionWarningsInfoQuery,
} = incomingTransactionsListApi;

export const {
  endpoints: {
    getIncomingList,
    getIncomingTransactionById,
    getIncomingTransactionAmlById,
    getIncomingTransactionValidators,
    checkIncomingTransaction,
    getIncomingTransactionWarnings,
    applyIncomingTransactionWarningAction,
    updateIncomingTransaction,
    getIncomingListStatuses,
    exportIncomingTransaction,
    getTransactionsExternalAccountsList,
    resetIncomingTransactionWarningAction,
    getIncomingTransactionWarning,
    getIncomingTransactionWarningsInfo,
  },
} = incomingTransactionsListApi;
